import { useEffect, useRef, useState } from 'react'
import { api, auth } from '../lib/api.js'
import { QUICK_REPLY_LIMIT, textLen } from '../lib/textLimit.js'
import { drawPoster, plainQr } from '../lib/poster.js'
import { confirmBox, alertBox } from '../components/Dialog.jsx'
import { onChange } from '../lib/changes.js'
import { beijingDateParts, beijingDateString, formatBeijingTime } from '../lib/time.js'
import { balanceWeight, evenWeights, rebaseWeights } from '../lib/dispatchWeights.js'
import FreeApply from '../components/FreeApply.jsx'

const TABS = [
  { key: 'agents', label: '运营账号' },
  { key: 'orders', label: '订单同步' },
  { key: 'replies', label: '快捷话术' },
  { key: 'channels', label: '渠道方案' },
  { key: 'shifts', label: '排班表' },
  { key: 'signedDispatch', label: '每日签收派单' },
  { key: 'livecode', label: '客服二维码' },
  { key: 'freeApply', label: '信用卡' },
  { key: 'pets', label: '宠物规则' },
  { key: 'skill', label: 'Prompt' },
  { key: 'skill_ext', label: 'Skill 扩展' },
]

export default function Settings() {
  const [tab, setTab] = useState('agents')
  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100 }}>
      <div style={{ fontSize: 20, fontWeight: 600 }}>设置中心</div>
      <div className="muted small" style={{ marginTop: 4, marginBottom: 20 }}>账号、话术与渠道配置</div>

      <div className="row" style={{ gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} className="btn btn-sm" onClick={() => setTab(t.key)}
            style={t.key === tab ? { background: 'var(--brand)', borderColor: 'var(--brand)', color: '#fff' } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'agents' && <Agents />}
      {tab === 'orders' && <OrderMapping />}
      {tab === 'replies' && <QuickReplies />}
      {tab === 'channels' && <Channels />}
      {tab === 'shifts' && <Shifts />}
      {tab === 'signedDispatch' && <SignedDispatch />}
      {tab === 'livecode' && <LiveCode />}
      {tab === 'freeApply' && <FreeApply />}
      {tab === 'pets' && <PetRules />}
      {tab === 'skill' && <SkillPanel />}
      {tab === 'skill_ext' && <SkillSettingsPanel />}
    </div>
  )
}

function OrderMapping() {
  const [state, setState] = useState(null)
  const [agents, setAgents] = useState([])
  const [setup, setSetup] = useState({ mappings: [], people: [] })
  const [agentId, setAgentId] = useState('')
  const [cwtId, setCwtId] = useState('')
  const [kind, setKind] = useState('primary')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEffect(() => {
    let alive = true
    async function load() {
      try {
        const status = await api.getOrderSyncStatus()
        if (!alive) return
        setState(status)
        if (status.enabled) {
          const [people, config] = await Promise.all([api.listAgents(), api.getOrderSyncSetup()])
          if (alive) { setAgents(people); setSetup(config) }
        }
      } catch (e) { if (alive) setErr(e.message) }
    }
    load()
    return () => { alive = false }
  }, [])
  async function update(params) {
    setBusy(true); setErr('')
    try { setSetup(await api.getOrderSyncSetup({ ...params, roster: true })) }
    catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  return (
    <section>
      <h3 style={{ fontSize: 16 }}>车网通运营绑定</h3>
      <p className="muted small">{state ? state.enabled ? (state.writes ? '外部写入已启用' : '外部写入未启用') : '订单同步未启用' : '加载中…'}</p>
      {err && <div className="err" role="alert">{err}</div>}
      {state?.enabled && <>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <select className="input" style={{ width: 200, maxWidth: '100%' }} aria-label="客服运营" value={agentId} onChange={e => setAgentId(e.target.value)}>
            <option value="">选择客服运营</option>
            {agents.filter(a => a.active !== false).map(a => <option key={a.id} value={a.id}>{a.name}（{a.id}）</option>)}
          </select>
          <select className="input" style={{ width: 200, maxWidth: '100%' }} aria-label="绑定类型" value={kind} onChange={e => setKind(e.target.value)}>
            <option value="primary">主ID（运营本人）</option>
            <option value="additional">附加ID（离职人员）</option>
          </select>
          <select className="input" style={{ width: 220, maxWidth: '100%' }} aria-label="车网通运营" value={cwtId} onChange={e => setCwtId(e.target.value)}>
            <option value="">选择车网通运营</option>
            {setup.people.map(p => <option key={p.id} value={p.id}>{p.name}（{p.id}）</option>)}
          </select>
          <button className="btn btn-sm" disabled={busy} onClick={() => update({})}>读取运营名单</button>
          <button className="btn btn-primary btn-sm" disabled={busy || !agentId || !cwtId}
            onClick={() => update({ agentId, cwtId, ...(kind === 'additional' ? { additional: true } : {}) })}>
            {kind === 'additional' ? '添加附加ID' : '保存主ID'}
          </button>
        </div>
        <p className="muted small" style={{ marginTop: -8 }}>
          主ID是运营本人的车网通账号。附加ID用于离职人员：这些ID名下的订单，在系统里归这位运营（进线时显示归属为他）；只有成交（人工成交、AI 成交、信用卡）核验通过后，才会在车网通里转到他的主ID，其他情况不转；成交后又标失败的，仍属于主ID。
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 0 }}><thead><tr><th>客服运营</th><th>车网通运营 ID</th><th>类型</th><th></th></tr></thead><tbody>
            {setup.mappings.map(m => (
              <tr key={m.cwt_id}>
                <td>{agents.find(a => a.id === m.agent_id)?.name || m.agent_id}</td>
                <td>{m.cwt_id}{setup.people.find(p => p.id === m.cwt_id) ? `（${setup.people.find(p => p.id === m.cwt_id).name}）` : ''}</td>
                <td>{m.is_primary === false ? '附加ID' : '主ID'}</td>
                <td>{m.is_primary === false && (
                  <button className="btn btn-sm" disabled={busy}
                    onClick={async () => { if (await confirmBox(`确认解绑附加ID ${m.cwt_id}？解绑后该ID名下的订单会重新核对归属。`)) update({ unbindCwtId: m.cwt_id }) }}>解绑</button>
                )}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <p className="muted small">待同步 {state.pending} · 未绑定会话 {state.unbound} · 待核对历史成交 {state.legacyUnboundSales || 0}</p>
      </>}
    </section>
  )
}

// ============ 运营账号 ============
function Agents() {
  const [list, setList] = useState([])
  const [editing, setEditing] = useState(null) // null=关闭, {}=新增, {...}=编辑
  const [err, setErr] = useState('')
  const [onlyActive, setOnlyActive] = useState(true)   // 筛选：只显示没停用的（默认开）

  const load = () => api.listAgents().then(setList).catch(e => setErr(e.message))
  // 上线/下线/接单开关这些写入后端会立刻通知（见 lib/changes.js）；心跳超时掉线不经过写入，靠 60 秒兜底
  useEffect(() => {
    load()
    const off = onChange(load)
    const t = setInterval(load, 60 * 1000)
    return () => { off(); clearInterval(t) }
  }, [])

  async function del(a) {
    if (a.id === auth.agent?.id) return alertBox('不能删除当前登录的账号')
    if (!(await confirmBox(`确认删除账号「${a.name || a.id}」？删除后该账号无法登录。`))) return
    try { await api.deleteAgent(a.id); load() } catch (e) { alertBox('删除失败：' + e.message) }
  }

  async function toggleActive(a) {
    if (a.id === auth.agent?.id) return alertBox('不能停用当前登录的账号')
    const next = !a.active
    if (next === false && !(await confirmBox(`确认停用账号「${a.name || a.id}」？停用后该账号无法登录，名下未成交的客户会自动转给其他在班运营。`))) return
    try { await api.setAgentActive(a.id, next); load() } catch (e) { alertBox('操作失败：' + e.message) }
  }

  const shown = onlyActive ? list.filter(a => a.active !== false) : list

  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="spread" style={{ marginBottom: 16 }}>
        <strong>运营账号</strong>
        <div>
          <label className="small" style={{ marginRight: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)} style={{ marginRight: 4 }} />
            只显示没停用的
          </label>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing({})}>+ 新增账号</button>
        </div>
      </div>
      {err && <div className="err">{err}</div>}
      {shown.length === 0 ? <div className="empty">{list.length === 0 ? '暂无账号' : '没有未停用的账号'}</div> : (
        <table>
          <thead><tr><th>名称</th><th>账号ID</th><th>角色</th><th>最大并发</th><th>状态</th><th style={{ textAlign: 'right' }}>操作</th></tr></thead>
          <tbody>
            {shown.map(a => (
              <tr key={a.id} style={a.active === false ? { opacity: .55 } : undefined}>
                <td>
                  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', marginRight: 8, background: a.online ? 'var(--ok)' : '#d1d5db' }} />
                  {a.name || a.id}
                </td>
                <td className="mono">{a.id}</td>
                <td>{a.role === 'admin' ? <span className="tag tag-ai">管理员</span> : a.role === 'aftersales' ? <span className="tag">售后</span> : <span className="tag">客服</span>}</td>
                <td>{a.max_concurrent ?? 5}</td>
                <td className="small">
                  {a.active === false ? <span style={{ color: 'var(--danger)', fontWeight: 500 }}>已停用</span>
                    : !a.has_password ? <span className="muted">未设密码</span>
                    : a.online ? <span style={{ color: 'var(--ok)', fontWeight: 500 }}>在线</span>
                    : <span className="muted">离线</span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn-link" onClick={() => setEditing(a)}>编辑</button>
                  <button className="btn-link" onClick={() => toggleActive(a)}>{a.active === false ? '启用' : '停用'}</button>
                  <button className="btn-link" style={{ color: 'var(--danger)' }} onClick={() => del(a)}>删除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="small muted" style={{ marginTop: 12, padding: 10, background: '#fffbeb', borderRadius: 8, color: '#b45309' }}>
        账号ID 是登录凭据。最大并发限制该客服同时接待的会话数，超出后系统不再向其派单。
        「停用」保留账号和历史记录只是禁止登录、退出接单，名下客户自动转给其他在班运营；「删除」不可恢复。
      </div>
      {editing && <AgentModal agent={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />}
    </div>
  )
}

function AgentModal({ agent, onClose, onSaved }) {
  const isNew = !agent.id
  const [f, setF] = useState({
    id: agent.id || '', name: agent.name || '', password: '',
    max_concurrent: agent.max_concurrent ?? 5, role: agent.role || 'agent',
  })
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  async function save() {
    setErr('')
    if (isNew && !f.id.trim()) return setErr('请填写账号ID')
    if (isNew && f.password.length < 8) return setErr('初始密码至少 8 位')
    if (!isNew && f.password && f.password.length < 8) return setErr('新密码至少 8 位')
    setBusy(true)
    try {
      const p = { name: f.name || f.id, max_concurrent: f.max_concurrent, role: f.role }
      if (isNew) await api.createAgent({ ...p, id: f.id.trim(), password: f.password })
      else await api.updateAgent({ ...p, id: agent.id, ...(f.password ? { password: f.password } : {}) })
      onSaved()
    } catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>{isNew ? '新增运营账号' : `编辑账号 ${agent.id}`}</h2>
        <div className="field">
          <label>账号 ID</label>
          <input className="input" value={f.id} onChange={set('id')} disabled={!isNew} placeholder="例如 zhang_san" />
          {isNew && <div className="small muted" style={{ marginTop: 5 }}>登录用，字母/数字/下划线/横线，创建后不可改</div>}
        </div>
        <div className="field">
          <label>显示名称</label>
          <input className="input" value={f.name} onChange={set('name')} placeholder="例如 张三" />
        </div>
        <div className="field">
          <label>{isNew ? '初始密码' : '重置密码（留空则不改）'}</label>
          <input className="input" type="password" value={f.password} onChange={set('password')} placeholder="至少 8 位" />
        </div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>角色</label>
            <select className="input" value={f.role} onChange={set('role')}>
              <option value="agent">客服（仅工作台）</option>
              <option value="aftersales">售后（只处理标为失败的会话，不计统计）</option>
              <option value="admin">管理员（含设置中心）</option>
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>最大并发</label>
            <input className="input" type="number" min="1" max="99" value={f.max_concurrent} onChange={set('max_concurrent')} />
          </div>
        </div>
        <div className="err">{err}</div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>保存</button>
        </div>
      </div>
    </div>
  )
}

// ============ 快捷话术 ============
function QuickReplies() {
  const [all, setAll] = useState([])
  const [editing, setEditing] = useState(null)
  const list = all.filter(r => !r.agent_id) // 这个页面只管共享库，个人的在工作台自己管
  const load = () => api.listQuickReplies().then(setAll).catch(() => {})
  useEffect(() => { load() }, [])

  async function del(r) {
    if (!(await confirmBox('确认删除该话术？'))) return
    await api.deleteQuickReply(r.id).catch(e => alertBox(e.message))
    load()
  }

  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="spread" style={{ marginBottom: 16 }}>
        <strong>快捷话术（全员共享）</strong>
        <button className="btn btn-primary btn-sm" onClick={() => setEditing({})}>+ 新增共享话术</button>
      </div>
      <div className="small muted" style={{ marginBottom: 12 }}>
        这里维护的是全员共享库。每个人自己的个人快捷话术，在工作台聊天框「快捷话术」面板里自己增删，这里看不到也管不到。
      </div>
      {list.length === 0 ? <div className="empty">暂无话术，客服可在工作台一键插入</div> : (
        <table>
          <thead><tr><th>快捷码</th><th>分类</th><th>内容</th><th>使用</th><th style={{ textAlign: 'right' }}>操作</th></tr></thead>
          <tbody>
            {list.map(r => (
              <tr key={r.id}>
                <td className="mono">{r.shortcut || '—'}</td>
                <td>{r.category || '—'}</td>
                <td style={{ maxWidth: 400 }} className="small">{r.content.slice(0, 60)}{r.content.length > 60 ? '…' : ''}</td>
                <td className="muted small">{r.used_count || 0} 次</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn-link" onClick={() => setEditing(r)}>编辑</button>
                  <button className="btn-link" style={{ color: 'var(--danger)' }} onClick={() => del(r)}>删除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && <ReplyModal reply={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />}
    </div>
  )
}

// ownerId：新建时建到谁名下。工作台「我的话术」传自己的 id；不传 = 系统话术（只有管理员能建）。
// 不传的话管理员在「我的话术」里新建会落进系统话术（2026-09-30 踩过）
export function ReplyModal({ reply, ownerId, onClose, onSaved }) {
  const personal = Boolean(ownerId || reply.agent_id)
  const [f, setF] = useState({
    shortcut: reply.shortcut || '', category: reply.category || '', content: reply.content || '',
  })
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  async function save() {
    if (!f.content.trim()) return setErr('内容不能为空')
    if (personal && textLen(f.content) > QUICK_REPLY_LIMIT) return setErr(`个人话术不能超过 ${QUICK_REPLY_LIMIT} 字`)
    try {
      await api.upsertQuickReply({ ...f, id: reply.id, ...(!reply.id && ownerId ? { agent_id: ownerId } : {}) })
      onSaved()
    } catch (e) { setErr(e.message) }
  }

  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>{reply.id ? '编辑话术' : '新增话术'}</h2>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>快捷码</label>
            <input className="input" value={f.shortcut} onChange={set('shortcut')} placeholder="如 /tk" />
            <div className="small muted" style={{ marginTop: 4 }}>工作台输入框里打出一模一样的码会立刻自动展开成正文，建议以 / 开头避免跟正常聊天内容撞上</div>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>分类</label>
            <input className="input" value={f.category} onChange={set('category')} placeholder="如 退款/拒付" />
            <div className="small muted" style={{ marginTop: 4 }}>列表按分类分组显示；要分子类就用"父类/子类"这样写</div>
          </div>
        </div>
        <div className="field">
          <label>话术内容</label>
          <textarea className="input" rows={5} value={f.content} onChange={set('content')}
            {...(personal ? { maxLength: QUICK_REPLY_LIMIT } : {})} />
          <div className="small" style={{ marginTop: 4, textAlign: 'right',
            color: personal && textLen(f.content) > QUICK_REPLY_LIMIT ? 'var(--danger)' : 'var(--muted)' }}>
            {personal ? `${textLen(f.content)} / ${QUICK_REPLY_LIMIT} 字（不含空格换行）` : `${textLen(f.content)} 字（不限字数）`}
          </div>
        </div>
        <div className="err">{err}</div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn btn-primary" onClick={save}
            disabled={personal && textLen(f.content) > QUICK_REPLY_LIMIT}>保存</button>
        </div>
      </div>
    </div>
  )
}

// ============ 渠道 ============
function Channels() {
  const [list, setList] = useState([])
  const [err, setErr] = useState('')
  const load = () => api.listChannels().then(setList).catch(e => setErr(e.message))
  useEffect(() => { load() }, [])

  return (
    <div>
      {err && <div className="err">{err}</div>}
      <div className="small muted" style={{ marginBottom: 14, padding: 10, background: '#eff6ff', borderRadius: 8, color: '#1e40af' }}>
        一个渠道就是一套<strong>方案</strong>：客户扫码入口不变，设为「当前方案」的那个决定 AI 用哪套话术、发哪条欢迎语。
        同时只能有一个方案生效，切换后新进线的客户立即走新话术；<strong>已经在聊的客户继续走原方案</strong>，不会聊到一半换卖法。
        保存即生效，不需要重启。
      </div>
      {list.map(c => <ChannelCard key={c.id} ch={c} onSaved={load} />)}
    </div>
  )
}

const SCRIPT_TEMPLATE = `【身份门】请您提供一下预约时的车牌号或签收快递的手机号，方便这边帮您查询。
【资料清单】尊敬的车主，激活需要的资料：1、您的是「客车」还是「货车」呢？2、身份证、车辆行驶证（原件或照片）3、收到的ETC设备 4、移步到车辆旁边 请问您资料都在身边吗？
【两档报价】目前ETC免费的激活方式有两种：1、360元保证金权益版（钱在您ETC账户，每月领10元共36个月；送500加油金+500洗车金；1.2万高速保障；可换车换牌）；2、指定银行信用卡0元激活（要审批、时间长，不推荐）。您选哪一种？[发图:免费办理表]
【激活引导】微信搜「车路网畅行」小程序，点服务再点去激活，按流程操作就可以。
【验证失败】设备侧边开关拨到蓝牙档，手机蓝牙和定位打开，微信允许使用蓝牙，再点重新验证；进到下一步告诉我。
【拒办挽留1】我们已经有这么多办理方式了，想了解一下您不办理的原因是什么呢？
【67押金】67是设备押金，激活成功后凭微信绿色收款码退回；未激活退货，仓库签收后1–3个工作日退回。
【转人工】这个我需要帮您转人工核实一下。`

function ChannelCard({ ch, onSaved }) {
  const [name, setName] = useState(ch.name || '')
  const [welcome, setWelcome] = useState(ch.welcome || '')
  const [scripts, setScripts] = useState(ch.reply_scripts || '')
  const [showScripts, setShowScripts] = useState(false)
  const [state, setState] = useState('')   // '' | saving | saved | 错误文案

  useEffect(() => {
    setName(ch.name || ''); setWelcome(ch.welcome || ''); setScripts(ch.reply_scripts || '')
  }, [ch.name, ch.welcome, ch.reply_scripts])

  const dirty = name !== (ch.name || '') || welcome !== (ch.welcome || '')
    || scripts !== (ch.reply_scripts || '')

  async function save(extra = {}) {
    setState('saving')
    try {
      await api.updateChannel({ id: ch.id, name, welcome, reply_scripts: scripts, ...extra })
      setState('saved')
      setTimeout(() => setState(''), 1800)
      onSaved?.()
    } catch (e) { setState(e.message) }
  }

  return (
    <div className="card" style={{ padding: 20, marginBottom: 14 }}>
      <div className="spread" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div className="row" style={{ gap: 8 }}>
          <input className="input" style={{ width: 190, fontWeight: 600 }}
            value={name} onChange={e => setName(e.target.value)} />
          <span className="tag mono">{ch.id}</span>
          {ch.enabled && <span className="tag" style={{ background: '#166534', color: '#fff' }}>当前方案</span>}
          {ch.conv_count > 0
            ? <span className="tag" style={{ background: '#dcfce7', color: '#166534' }}>{ch.conv_count} 个会话</span>
            : <span className="tag" style={{ background: 'var(--hover)', color: '#9ca3af' }}>未使用</span>}
        </div>
        <div className="row" style={{ gap: 10 }}>
          {state === 'saving' && <span className="small muted">保存中…</span>}
          {state === 'saved' && <span className="small" style={{ color: 'var(--ok)' }}>已保存，已生效</span>}
          {state && !['saving', 'saved'].includes(state) && <span className="small" style={{ color: 'var(--danger)' }}>{state}</span>}
          {/* 方案是单选：启用一个会自动停掉其余，所以不做成复选框 */}
          <button className="btn btn-sm" disabled={!!ch.enabled || state === 'saving'}
            onClick={() => save({ enabled: true })}
            style={ch.enabled ? {} : { borderColor: 'var(--brand)', color: 'var(--brand)' }}>
            {ch.enabled ? '生效中' : '设为当前方案'}
          </button>
        </div>
      </div>

      <div className="field" style={{ marginBottom: 10 }}>
        <label>进线欢迎语</label>
        <textarea className="input" rows={13} value={welcome}
          onChange={e => setWelcome(e.target.value)}
          placeholder="留空则使用系统内置的兜底文案"
          style={{ lineHeight: 1.7, fontSize: 13.5, resize: 'vertical' }} />
        <div className="spread" style={{ marginTop: 6 }}>
          <span className="small muted">
            {welcome.length} 字 · {welcome.split('\n').length} 行 · 换行和空行会原样发给客户
          </span>
          <div className="row" style={{ gap: 8 }}>
            {dirty && (
              <button className="btn btn-sm" onClick={() => { setName(ch.name || ''); setWelcome(ch.welcome || '') }}>
                撤销
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => save()} disabled={!dirty || state === 'saving'}>
              {dirty ? '保存' : '已是最新'}
            </button>
          </div>
        </div>
      </div>

      <div className="field">
        <div className="spread" style={{ marginBottom: 6 }}>
          <label style={{ margin: 0 }}>AI 回复话术文案</label>
          <button className="btn btn-sm" onClick={() => setShowScripts(v => !v)}>
            {showScripts ? '收起' : scripts ? `展开编辑（${scripts.length} 字）` : '展开编辑（未设置，用 prompt 里的文案）'}
          </button>
        </div>
        {showScripts && (
          <>
            <textarea className="input" rows={14} value={scripts}
              onChange={e => setScripts(e.target.value)}
              placeholder={SCRIPT_TEMPLATE}
              style={{ lineHeight: 1.65, fontSize: 12.5, resize: 'vertical' }} />
            <div className="small muted" style={{ marginTop: 6 }}>
              这里写客户实际看到的话术；系统会把它拼在 Prompt 之后、按最新版优先使用。发图写成 <code>[发图:素材名]</code>。留空则沿用 Prompt 里的旧文案。
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <button className="btn btn-sm" onClick={() => setScripts(SCRIPT_TEMPLATE)}>填入模板</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ============ 每日签收派单 ============
// 日控只改「后续新签收单」的派发策略；不会回收或重分已经派出的会话。
function SignedDispatch() {
  const [day, setDay] = useState(beijingDateString())
  const [status, setStatus] = useState(null)
  const [weights, setWeights] = useState({})
  const [lockedIds, setLockedIds] = useState([])   // 锁定比例的人：改别人时他的比例保持不动（仅页面内临时状态，不入库）
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const dayRef = useRef(day)
  const requestRef = useRef(0)
  const balanceBase = useRef(null)   // 开始编辑某人比例时的快照：输入过程中其余人始终按这份原比例分配

  const isAdmin = auth.isAdmin
  const people = status?.people || []
  const mode = status?.mode || 'smart'
  const pausedIds = Array.isArray(status?.control?.pauseNew) ? status.control.pauseNew.map(String) : people.filter(p => p.pauseNew).map(p => String(p.id))
  // 手动比例严格只覆盖未来新单 active pool：不含长期移出、停用或本日停派的人。
  const poolFor = (pauseIds = pausedIds, list = people) => list.filter(p => !p.excluded && p.active !== false && !pauseIds.includes(String(p.id)))
  const editablePeople = poolFor()
  const weightValues = editablePeople.map(p => Number(weights[p.id]))
  const sum = weightValues.reduce((total, value) => total + value, 0)
  const weightsValid = editablePeople.length > 0 && weightValues.every(value => Number.isFinite(value) && value >= 0 && value <= 100) && Math.abs(sum - 100) < 0.005

  const displayPercent = value => {
    const n = Number(value)
    return Number.isFinite(n) ? `${n.toFixed(2)}%` : '—'
  }
  const numberWeight = value => {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
  }
  const pauseIdsOf = next => Array.isArray(next?.control?.pauseNew) ? next.control.pauseNew.map(String) : (next?.people || []).filter(p => p.pauseNew).map(p => String(p.id))
  const defaultWeights = (list, source = 'manualRatio', pauseIds = pausedIds) => Object.fromEntries(poolFor(pauseIds, list)
    .map(p => [p.id, numberWeight(p[source])]))

  function weightsFromStatus(next = status) {
    const configured = next?.control?.weights
    const pool = poolFor(pauseIdsOf(next), next?.people || [])
    return configured && typeof configured === 'object' ? Object.fromEntries(
      pool.map(p => [p.id, numberWeight(configured[p.id])]),
    ) : defaultWeights(next?.people || [], 'manualRatio', pauseIdsOf(next))
  }

  function validateWeights(source, scope = editablePeople) {
    const values = scope.map(p => Number(source[p.id]))
    const sourceSum = values.reduce((total, value) => total + value, 0)
    const sourceValid = scope.length > 0 && values.every(value => Number.isFinite(value) && value >= 0 && value <= 100) && Math.abs(sourceSum - 100) < 0.005
    if (!sourceValid) return { valid: false, sum: sourceSum, weights: null }
    let allocated = 0
    const rounded = Object.fromEntries(scope.map((p, index) => {
      const value = index === scope.length - 1 ? Number((100 - allocated).toFixed(2)) : Number(Number(source[p.id]).toFixed(2))
      allocated += value
      return [p.id, value]
    }))
    const roundedValues = Object.values(rounded)
    const roundedSum = roundedValues.reduce((total, value) => total + value, 0)
    return { valid: roundedValues.every(value => Number.isFinite(value) && value >= 0 && value <= 100) && Math.abs(roundedSum - 100) < 0.001, sum: sourceSum, weights: rounded }
  }

  function hydrate(next) {
    setStatus(next)
    setWeights(weightsFromStatus(next))
  }

  async function load(forDay = day, { keepPreview = false } = {}) {
    const requestId = ++requestRef.current
    setErr('')
    try {
      const next = await api.getSignedDispatchStatus({ day: forDay })
      if (dayRef.current !== forDay || requestRef.current !== requestId) return
      hydrate(next)
      if (!keepPreview) setPreview(null)
    } catch (e) {
      if (dayRef.current !== forDay || requestRef.current !== requestId) return
      setStatus(null)
      setErr(`每日签收派单暂不可用：${e.message}`)
    }
  }

  useEffect(() => {
    dayRef.current = day
    setStatus(null); setPreview(null); setLockedIds([])
    load(day)
  }, [day])

  function resetSmart() {
    const smart = defaultWeights(people, 'smartRatio')
    setWeights(rebaseWeights(smart, editablePeople))
  }

  function enterManual() {
    if (mode === 'manual') return
    const next = rebaseWeights(defaultWeights(people, 'smartRatio'), editablePeople)
    setWeights(next)
    save('manual', pausedIds, next, editablePeople)
  }

  function splitEvenly() {
    setWeights(evenWeights(editablePeople))
  }

  function halveWeight(id) {
    balanceBase.current = null
    setWeights(balanceWeight(weights, editablePeople, id, Math.max(0, Number(weights[id] || 0)) / 2, lockedIds))
  }

  function toggleLock(id, checked) {
    setLockedIds(list => checked ? [...new Set([...list, String(id)])] : list.filter(value => value !== String(id)))
  }

  // 改一个人：其余人按开始编辑时的原比例自动分剩下的，合计始终 100.00%。输入中保留原样文字，失焦后定为两位小数。
  function changeWeight(id, raw, final = false) {
    const value = Number(raw)
    const next = balanceWeight(balanceBase.current || weights, editablePeople, id, value, lockedIds)
    setWeights(final || value > 100 || value < 0 ? next : { ...next, [id]: raw })
    if (final) balanceBase.current = null
  }

  async function save(nextMode = mode, nextPauseNew = pausedIds, nextWeights = weights, scope = poolFor(nextPauseNew)) {
    if (!isAdmin || !status) return
    const requestDay = day
    const requestStatus = status
    const normalized = validateWeights(nextWeights, scope)
    if (nextMode === 'manual' && !normalized.valid) return setErr(`手动比例需逐项为 0–100%，且合计等于 100.00%；当前为 ${normalized.sum.toFixed(2)}%`)
    requestRef.current += 1
    setBusy('save'); setErr('')
    try {
      const next = await api.setSignedDispatchDailyControl({
        day: requestDay,
        expectedRev: requestStatus.rev,
        mode: nextMode,
        pauseNew: nextPauseNew,
        weights: nextMode === 'manual' ? normalized.weights : undefined,
        reason: '设置中心每日签收派单',
      })
      if (dayRef.current === requestDay) { hydrate(next); setPreview(null) }
    } catch (e) { if (dayRef.current === requestDay) setErr(e.message) } finally { if (dayRef.current === requestDay) setBusy('') }
  }

  function togglePause(id, checked) {
    const next = checked ? [...new Set([...pausedIds, String(id)])] : pausedIds.filter(value => value !== String(id))
    const nextScope = poolFor(next)
    const nextWeights = rebaseWeights(weightsFromStatus(), nextScope)
    save(mode, next, nextWeights, nextScope)
  }

  async function clearControl() {
    if (!status || !(await confirmBox('确认清除本日派单控制？将恢复系统默认智能派单，已派单不会重分。'))) return
    const requestDay = day
    const requestStatus = status
    requestRef.current += 1
    setBusy('clear'); setErr('')
    try {
      const next = await api.clearSignedDispatchDailyControl({ day: requestDay, expectedRev: requestStatus.rev })
      if (dayRef.current === requestDay) { hydrate(next); setPreview(null) }
    } catch (e) { if (dayRef.current === requestDay) setErr(e.message) } finally { if (dayRef.current === requestDay) setBusy('') }
  }

  async function showPreview() {
    const requestDay = day
    setBusy('preview'); setErr('')
    try {
      const next = await api.getSignedDispatchPreview({ day: requestDay })
      if (dayRef.current === requestDay) setPreview(next)
    } catch (e) { if (dayRef.current === requestDay) setErr(`预览失败：${e.message}`) } finally { if (dayRef.current === requestDay) setBusy('') }
  }

  async function reconcile() {
    const requestDay = day
    const requestStatus = status
    setBusy('reconcile-preview'); setErr('')
    try {
      const plan = await api.reconcileSignedDispatchPreview({ day: requestDay })
      if (dayRef.current !== requestDay) return
      setPreview(plan)
      const summary = plan?.summary || plan?.warnings?.join('；') || '将按当前控制同步手工分配。'
      if (!(await confirmBox(`以下操作会提交同步手工分配：\n${summary}\n\n只处理后续新签收单；已派单保留、不重分。确认继续？`))) return
      if (dayRef.current !== requestDay) return
      setBusy('reconcile-commit')
      const result = await api.reconcileSignedDispatchCommit({
        day: requestDay,
        expectedRev: plan.expectedRev ?? plan.rev ?? requestStatus?.rev,
        items: plan.items || plan.next || [],
      })
      if (dayRef.current !== requestDay) return
      setPreview(result)
      await load(requestDay, { keepPreview: true })
      await alertBox('手工分配已同步')
    } catch (e) { if (dayRef.current === requestDay) setErr(`同步失败：${e.message}`) } finally { if (dayRef.current === requestDay) setBusy('') }
  }

  const notificationText = value => {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'string' || typeof value === 'number') return String(value)
    if (value.ok === true || value.enabled === true) return '正常'
    if (value.ok === false || value.enabled === false) return '异常'
    return value.message || value.status || '已获取'
  }
  const isPaused = p => pausedIds.includes(String(p.id))
  const statusLabel = p => p.excluded ? '长期移出' : p.active === false ? '已停用' : isPaused(p) ? '暂停新签收' : '正常派单'
  const statusColor = p => p.excluded || p.active === false ? 'var(--danger)' : isPaused(p) ? '#b45309' : 'var(--ok)'

  return (
    <section>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="spread" style={{ gap: 14, flexWrap: 'wrap' }}>
          <div>
            <strong>每日签收派单</strong>
            <div className="small muted" style={{ marginTop: 5 }}>北京日期维度控制后续新签收单；已派单保留，不做重分。</div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <label className="small muted" htmlFor="signed-dispatch-day">北京日期</label>
            <input id="signed-dispatch-day" className="input" type="date" value={day} disabled={Boolean(busy)} onChange={e => setDay(e.target.value)} style={{ width: 154 }} />
            <button className="btn btn-sm" disabled={Boolean(busy)} onClick={() => load()}>刷新</button>
          </div>
        </div>
        {err && <div className="err" role="alert">{err}</div>}
        {!status && !err && <div className="muted" style={{ paddingTop: 18 }}>加载中…</div>}
        {status && <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 10, marginTop: 18 }}>
            <DispatchMetric label="当前模式" value={mode === 'manual' ? '手动' : '智能'} tone={mode === 'manual' ? '#b45309' : 'var(--brand)'} />
            <DispatchMetric label="下一批时间" value={status.nextRunAt ? formatBeijingTime(status.nextRunAt, { hour: '2-digit', minute: '2-digit', month: 'numeric', day: 'numeric', hour12: false }) : '—'} />
            <DispatchMetric label="今日累计" value={`${people.reduce((n, p) => n + Number(p.dailyCount || 0), 0)} 单`} />
            <DispatchMetric label="通知状态" value={notificationText(status.notificationStatus)} />
          </div>
          <div className="small muted" style={{ marginTop: 12 }}>控制版本 rev {status.rev ?? '—'} · {pausedIds.length ? `已停 ${pausedIds.length} 人后续新签收单` : '正常接收后续新签收单'}</div>
        </>}
      </div>

      {status && <div className="card" style={{ overflow: 'hidden' }}>
        <div className="spread" style={{ padding: '16px 20px 12px', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <strong>人员目标</strong>
            <div className="small muted" style={{ marginTop: 4 }}>{mode === 'manual' ? '后续新签收单目标比例可编辑：改一个人，其余没锁定的人按原比例自动调整，合计保持 100.00%；勾选「锁定」的人比例不动；已派单不参与补偿' : '智能模式按预测比例展示，只读。'}</div>
          </div>
          {isAdmin && <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" disabled={Boolean(busy)} onClick={showPreview}>预览下一批</button>
            <button className="btn btn-sm" disabled={Boolean(busy)} onClick={reconcile}>同步手工分配</button>
          </div>}
        </div>

        {isAdmin && <div style={{ padding: '0 20px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <button className={`btn btn-sm ${mode === 'smart' ? 'btn-primary' : ''}`} disabled={Boolean(busy)} onClick={() => mode !== 'smart' && save('smart', pausedIds)}>智能模式</button>
            <button className={`btn btn-sm ${mode === 'manual' ? 'btn-primary' : ''}`} disabled={Boolean(busy)} onClick={enterManual}>手动模式</button>
            <span className="small muted">今日停派按人员设置：只停后续新签收单，已派单保留、不重分</span>
          </div>
          {mode === 'manual' && <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <button className="btn btn-sm" disabled={Boolean(busy)} onClick={resetSmart}>按智能预测重置</button>
            <button className="btn btn-sm" disabled={Boolean(busy)} onClick={splitEvenly}>平均分配</button>
            <span className="small" style={{ color: weightsValid ? 'var(--ok)' : 'var(--danger)', fontWeight: 600 }}>合计 {sum.toFixed(2)}%</span>
          </div>}
        </div>}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 840 }}>
            <thead><tr><th>人员</th><th>ID</th><th>今日已派</th><th>智能预测</th><th>后续新单目标</th><th>未来新单开关</th><th>状态</th></tr></thead>
            <tbody>{people.filter(p => !p.excluded).map(p => (
              <tr key={p.id}>
                <td><strong>{p.name || p.id}</strong>{p.cwtId && <div className="small muted">车网通 {p.cwtId}</div>}</td>
                <td className="mono">{p.id}</td>
                <td>{p.dailyCount ?? 0}</td>
                <td>{displayPercent(p.smartRatio)}</td>
                <td>{mode === 'manual' && editablePeople.some(x => x.id === p.id) && isAdmin ? <div className="row" style={{ gap: 6 }}><input className="input" type="number" min="0" max="100" step="0.01" value={weights[p.id] ?? ''} onFocus={() => { balanceBase.current = weights }} onChange={e => changeWeight(p.id, e.target.value)} onBlur={e => changeWeight(p.id, e.target.value, true)} disabled={lockedIds.includes(String(p.id))} style={{ width: 88, padding: '6px 8px' }} /><span className="small muted">%</span><button className="btn-link small" disabled={lockedIds.includes(String(p.id))} onClick={() => halveWeight(p.id)}>比例减半</button><label className="row small" style={{ gap: 4 }}><input type="checkbox" checked={lockedIds.includes(String(p.id))} onChange={e => toggleLock(p.id, e.target.checked)} />锁定</label></div> : displayPercent(weights[p.id] ?? p.manualRatio)}</td>
                <td>{isAdmin ? <label className="row small" style={{ gap: 6 }}><input type="checkbox" checked={isPaused(p)} disabled={Boolean(busy) || p.active === false} onChange={e => togglePause(p.id, e.target.checked)} />{isPaused(p) ? '已停新单' : '接收新单'}</label> : isPaused(p) ? <span style={{ color: '#b45309' }}>已停新单</span> : <span style={{ color: 'var(--ok)' }}>接收新单</span>}</td>
                <td><span className="tag" style={{ color: statusColor(p) }}>{statusLabel(p)}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        {isAdmin && <div className="row" style={{ padding: 16, gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" disabled={Boolean(busy) || (mode === 'manual' && !weightsValid)} onClick={() => save()}>{busy === 'save' ? '保存中…' : '保存本日控制'}</button>
          <button className="btn btn-danger" disabled={Boolean(busy)} onClick={clearControl}>{busy === 'clear' ? '清除中…' : '清除本日控制'}</button>
          {mode === 'manual' && !weightsValid && <span className="small" style={{ color: 'var(--danger)' }}>合计必须为 100.00% 才能保存</span>}
        </div>}
        {!isAdmin && <div className="small muted" style={{ padding: 16 }}>仅管理员可修改每日签收派单控制。</div>}
      </div>}

      {preview && <DispatchPreview preview={preview} />}
    </section>
  )
}

function DispatchMetric({ label, value, tone }) {
  return <div style={{ padding: '11px 12px', border: '1px solid var(--line)', borderRadius: 9, background: 'var(--hover)' }}><div className="small muted">{label}</div><div style={{ marginTop: 4, fontWeight: 600, color: tone || 'var(--text)' }}>{value}</div></div>
}

function DispatchPreview({ preview }) {
  const rows = Array.isArray(preview?.next) ? preview.next : Array.isArray(preview?.pool) ? preview.pool : []
  const warnings = Array.isArray(preview?.warnings) ? preview.warnings : []
  return <div className="card" style={{ marginTop: 16, padding: 20 }}>
    <strong>下一批预览</strong><span className="small muted" style={{ marginLeft: 8 }}>只读，不会写入派单</span>
    <div className="small muted" style={{ margin: '8px 0 12px' }}>候选池 {Array.isArray(preview?.pool) ? preview.pool.length : preview?.pool ?? '—'} · 活跃 {preview?.active ?? '—'}</div>
    {warnings.length > 0 && <div className="small" style={{ color: '#b45309', background: '#fffbeb', padding: 10, borderRadius: 8, marginBottom: 12 }}>注意：{warnings.join('；')}</div>}
    {rows.length > 0 ? <div style={{ overflowX: 'auto' }}><table><thead><tr><th>人员 / 项目</th><th>预测结果</th></tr></thead><tbody>{rows.slice(0, 12).map((row, index) => <tr key={row.id || row.agentId || index}><td>{row.name || row.agentName || row.id || row.agentId || `第 ${index + 1} 项`}</td><td>{row.count ?? row.dailyCount ?? row.target ?? JSON.stringify(row)}</td></tr>)}</tbody></table></div> : <div className="muted small">暂无下一批可展示的数据。</div>}
  </div>
}

// ============ 排班表 ============
// 自动派单靠它判断今天谁在班、什么班次、几点之后不再派给他。
// 用「粘贴」而不是上传 Excel：不用引入解析库，从 Excel/表格软件直接 Ctrl+C 粘过来就行，
// 复制过来的就是 TSV（制表符分隔），浏览器原生能处理。
const SHIFT_ALIAS = {
  白: 'day', 早: 'day', 白班: 'day', 早班: 'day', day: 'day',
  中: 'mid', 中班: 'mid', mid: 'mid',
  晚: 'night', 夜: 'night', 晚班: 'night', 夜班: 'night', night: 'night',
  休: 'off', 休息: 'off', off: 'off', '': 'off', '-': 'off',
}
const SHIFT_LABEL = { day: '白班', mid: '中班', night: '晚班', off: '休' }
const SHIFT_COLOR = { day: '#0ea5e9', mid: '#8b5cf6', night: '#d97706', off: 'var(--muted)' }

function Shifts() {
  const [agents, setAgents] = useState([])
  const [text, setText] = useState('')
  const [parsed, setParsed] = useState(null)
  const [err, setErr] = useState('')
  const [msg, setMsg] = useState('')
  const [rows, setRows] = useState([])

  const load = () => {
    const now = beijingDateParts()
    const from = `${now.year}-${now.month}-01`
    const end = new Date(Date.UTC(Number(now.year), Number(now.month), 0))
    const to = `${now.year}-${now.month}-${String(end.getUTCDate()).padStart(2, '0')}`
    api.listShifts({ from, to }).then(setRows).catch(e => setErr(e.message))
  }
  useEffect(() => { api.listAgents().then(setAgents).catch(() => {}); load() }, [])

  // 竖人横日期：第一行是日期，之后每行开头是姓名
  function parse() {
    setErr(''); setMsg(''); setParsed(null)
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim())
    if (lines.length < 2) return setErr('至少要有表头（日期行）和一行人员')
    const cells = lines.map(l => l.split(/\t|,|，/).map(c => c.trim()))
    const head = cells[0]
    // 表头里认得出的日期列：支持 2026-10-01 / 10-01 / 1 这几种写法
    const now = beijingDateParts()
    const dates = head.map((h, i) => {
      if (i === 0) return null
      let m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(h)
      if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
      m = /^(\d{1,2})[-/](\d{1,2})$/.exec(h)
      if (m) return `${now.year}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`
      m = /^(\d{1,2})$/.exec(h)
      if (m) return `${now.year}-${now.month}-${m[1].padStart(2, '0')}`
      return null
    })
    if (!dates.some(Boolean)) return setErr('表头里没认出日期列。日期支持 2026-10-01、10-01 或 1 这几种写法')

    const byName = {}
    for (const a of agents) byName[a.name] = a.id
    const out = []; const bad = []
    for (const row of cells.slice(1)) {
      const name = row[0]
      if (!name) continue
      const id = byName[name] || (agents.find(a => a.id === name)?.id)
      if (!id) { bad.push(name); continue }
      for (let i = 1; i < row.length; i++) {
        if (!dates[i]) continue
        const shift = SHIFT_ALIAS[row[i]]
        if (shift === undefined) { bad.push(`${name} 在 ${dates[i]} 的「${row[i]}」看不懂`); continue }
        out.push({ work_date: dates[i], agent_id: id, shift })
      }
    }
    if (bad.length) return setErr('这些对不上：' + bad.slice(0, 6).join('；') + (bad.length > 6 ? ` 等 ${bad.length} 处` : ''))
    if (!out.length) return setErr('没解析出任何排班')
    setParsed(out)
  }

  async function submit() {
    setErr(''); setMsg('')
    try {
      const r = await api.importShifts(parsed)
      setMsg(`已导入 ${r.count} 条，覆盖月份：${r.months.join('、')}`)
      setParsed(null); setText(''); load()
    } catch (e) { setErr(e.message) }
  }

  // 本月排班的网格视图
  const days = [...new Set(rows.map(r => r.work_date))].sort()
  const byAgent = {}
  for (const r of rows) (byAgent[r.agent_id] ||= {})[r.work_date] = r.shift
  const agentName = id => agents.find(a => a.id === id)?.name || id

  return (
    <div>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <strong>导入排班表</strong>
        <div className="muted small" style={{ margin: '6px 0 10px' }}>
          从 Excel 直接复制粘贴过来即可（竖人横日期）。第一行是日期，之后每行开头写姓名。
          班次填 白/中/晚/休（早班也认，空格算休息）。
          <b>按月整月覆盖</b>——导入 10 月会把库里原有的 10 月排班全部替换。
        </div>
        <div className="small muted" style={{ marginBottom: 8, fontFamily: 'monospace', background: 'var(--hover)', padding: 8, borderRadius: 6 }}>
          姓名　2026-10-01　2026-10-02　2026-10-03<br />
          宋涛　白　　　　中　　　　休<br />
          黄焯莹　中　　　晚　　　　白
        </div>
        <textarea className="input" rows={7} value={text} onChange={e => setText(e.target.value)}
          placeholder="在这里粘贴排班表…" style={{ fontFamily: 'monospace', fontSize: 13 }} />
        <div className="row" style={{ gap: 8, marginTop: 10 }}>
          <button className="btn btn-sm btn-primary" onClick={parse} disabled={!text.trim()}>解析预览</button>
          {parsed && (
            <button className="btn btn-sm btn-primary" onClick={submit}>
              确认导入 {parsed.length} 条
            </button>
          )}
          {parsed && <button className="btn btn-sm" onClick={() => setParsed(null)}>取消</button>}
        </div>
        {err && <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
        {msg && <div style={{ marginTop: 10, color: 'var(--ok)', fontSize: 13 }}>{msg}</div>}
        {parsed && (
          <div className="small muted" style={{ marginTop: 10 }}>
            预览：{[...new Set(parsed.map(p => p.agent_id))].length} 人 ×{' '}
            {[...new Set(parsed.map(p => p.work_date))].length} 天，
            其中休息 {parsed.filter(p => p.shift === 'off').length} 人次
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 20, overflowX: 'auto' }}>
        <strong style={{ display: 'block', marginBottom: 12 }}>本月排班</strong>
        {!days.length ? (
          <div className="empty">本月还没有排班数据</div>
        ) : (
          <table style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--card)' }}>姓名</th>
                {days.map(d => <th key={d} style={{ textAlign: 'center' }}>{d.slice(8)}</th>)}
              </tr>
            </thead>
            <tbody>
              {Object.keys(byAgent).map(id => (
                <tr key={id}>
                  <td style={{ position: 'sticky', left: 0, background: 'var(--card)', whiteSpace: 'nowrap' }}>{agentName(id)}</td>
                  {days.map(d => {
                    const s = byAgent[id][d]
                    return (
                      <td key={d} style={{ textAlign: 'center', color: SHIFT_COLOR[s] || 'var(--muted)' }}>
                        {s ? SHIFT_LABEL[s] : '·'}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="small muted" style={{ marginTop: 12 }}>
          白班 9:00-12:00 / 13:00-17:30（17:00 起不再派单）　
          中班 10:00-13:00 / 14:00-18:30（18:00 起不再派单）　
          晚班 13:00-18:00 / 18:30-21:00（20:30 起不再派单）
        </div>
      </div>
    </div>
  )
}

// ============ 客服二维码（活码）============
// 印出去的码永远不变，扫码跳到哪个微信客服在这里改。客服号被封时换个链接即可，码不用重印。
function LiveCode() {
  const [lc, setLc] = useState(null)
  const [url, setUrl] = useState('')
  const [note, setNote] = useState('')
  const [state, setState] = useState('')   // '' | saving | saved | 错误文案
  const [noteState, setNoteState] = useState('')
  const cvRef = useRef(null)

  useEffect(() => { api.getLiveCode().then(d => { setLc(d); setNote(d.note || '') }).catch(e => setState(e.message)) }, [])
  useEffect(() => { if (lc?.public_url && cvRef.current) drawPoster(cvRef.current, lc.public_url + '/') }, [lc?.public_url])

  // url 和文案分开保存，互相传对方当前的值，不然一个存了会把另一个带空/带旧（见后端 setLiveCode 注释）
  async function save() {
    setState('saving')
    try { const d = await api.setLiveCode(url.trim(), lc.note); setLc(d); setNote(d.note || ''); setUrl(''); setState('saved'); setTimeout(() => setState(''), 1800) }
    catch (e) { setState(e.message) }
  }
  async function saveNote() {
    setNoteState('saving')
    try { setLc(await api.setLiveCode(lc.url, note.trim())); setNoteState('saved'); setTimeout(() => setNoteState(''), 1800) }
    catch (e) { setNoteState(e.message) }
  }
  function download(href, name) { const a = document.createElement('a'); a.href = href; a.download = name; a.click() }

  if (!lc) return <div className="muted">{state || '加载中…'}</div>
  return (
    <div className="card" style={{ padding: 24, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 360px', minWidth: 0 }}>
        <div className="small muted" style={{ marginBottom: 14, padding: 10, background: 'var(--hover)', borderRadius: 8 }}>
          印出去的二维码永远不变，客户扫码后跳到下面这个微信客服链接。客服号被封了，在这里换成新链接，保存后下一次扫码就生效，码不用重印。
        </div>
        <div className="field">
          <label>当前跳转到</label>
          <div className="mono" style={{ padding: '9px 12px', background: 'var(--hover)', borderRadius: 8, wordBreak: 'break-all' }}>
            {lc.url || <span className="muted">还没设置，客户扫码会看到「客服暂未开放」</span>}
          </div>
          <div className="small muted" style={{ marginTop: 6 }}>
            最后修改：{lc.updated_at ? formatBeijingTime(lc.updated_at, { hour12: false }) : '—'}
          </div>
        </div>
        <div className="field">
          <label>换成新的客服链接</label>
          <input className="input" value={url} onChange={e => setUrl(e.target.value)}
            placeholder="微信客服后台复制的 https://work.weixin.qq.com/kfid/kfc… 链接" />
        </div>
        <div className="row">
          <button className="btn btn-primary" onClick={save} disabled={!url.trim() || state === 'saving'}>
            {state === 'saving' ? '保存中…' : '保存，立即生效'}
          </button>
          {state === 'saved' && <span className="small" style={{ color: 'var(--ok)' }}>已保存</span>}
          {state && state !== 'saving' && state !== 'saved' && <span className="small" style={{ color: 'var(--danger)' }}>{state}</span>}
        </div>
        <div className="field" style={{ marginTop: 20 }}>
          <label>落地页文案（显示在客户扫码看到的页面上，选填）</label>
          <textarea className="input" rows={3} value={note} onChange={e => setNote(e.target.value)}
            maxLength={200} style={{ resize: 'vertical' }}
            placeholder="比如工作时间、常见问题提示，不填就不显示这块" />
        </div>
        <div className="row">
          <button className="btn btn-sm btn-primary" onClick={saveNote} disabled={noteState === 'saving' || note === (lc.note || '')}>
            {noteState === 'saving' ? '保存中…' : '保存文案'}
          </button>
          {noteState === 'saved' && <span className="small" style={{ color: 'var(--ok)' }}>已保存</span>}
          {noteState && noteState !== 'saving' && noteState !== 'saved' && <span className="small" style={{ color: 'var(--danger)' }}>{noteState}</span>}
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <canvas ref={cvRef} style={{ width: 240, borderRadius: 12, border: '1px solid var(--line)' }} />
        <div className="small muted" style={{ margin: '6px 0 10px' }}>{lc.public_url}/</div>
        <div className="row" style={{ justifyContent: 'center' }}>
          <button className="btn btn-sm btn-primary" onClick={() => download(cvRef.current.toDataURL('image/png'), '客服二维码海报.png')}>下载海报</button>
          <button className="btn btn-sm" onClick={async () => download(await plainQr(lc.public_url + '/'), '客服二维码.png')}>下载纯二维码</button>
        </div>
      </div>
    </div>
  )
}

// ============ 宠物规则 ============
// 盲盒规则先把参数存下来。现在只有「新手盲盒稀有率」和宠物名字/稀有度在用，
// 积分抽盒、保底、重复返还等以后开发时直接读这里的数
function PetRules() {
  const [r, setR] = useState(null)
  const [labels, setLabels] = useState({})
  const [state, setState] = useState('')
  useEffect(() => { api.getPetRules().then(d => { setR(d.rules); setLabels(d.rarities) }).catch(e => setState(e.message)) }, [])
  if (!r) return <div className="muted">{state || '加载中…'}</div>
  const set = (k, v) => setR(x => ({ ...x, [k]: v }))
  const setIn = (k, sub, v) => setR(x => ({ ...x, [k]: { ...x[k], [sub]: v } }))
  const setPet = (id, k, v) => setR(x => ({ ...x, pets: x.pets.map(p => p.id === id ? { ...p, [k]: v } : p) }))
  const sum = Object.keys(labels).reduce((a, k) => a + Number(r.prob[k] || 0), 0)
  async function save() {
    setState('saving')
    try { const d = await api.setPetRules(r); setR(d.rules); setState('saved'); setTimeout(() => setState(''), 1800) }
    catch (e) { setState(e.message) }
  }
  // 普通函数而不是组件：写成组件的话每次渲染都是新组件，输入框每打一个字就失焦
  // 用普通函数不用组件：写成组件的话每次渲染都算新组件，输入框每打一个字就失焦
  const num = (v, on, suffix, w = 90, key) => (
    <span key={key} className="row" style={{ gap: 6 }}>
      <input className="input" type="number" value={v} onChange={e => on(e.target.value)} style={{ width: w }} />
      {suffix && <span className="small muted">{suffix}</span>}
    </span>
  )
  const row = (label, children, off) => (
    <div className="row" style={{ gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
      <div style={{ width: 130, fontSize: 13 }}>{label}{off && <div className="small muted">暂未启用</div>}</div>
      {children}
    </div>
  )
  return (
    <div className="card" style={{ padding: 24 }}>
      <div className="small muted" style={{ marginBottom: 16, padding: 10, background: 'var(--hover)', borderRadius: 8 }}>
        新员工第一次进工作台开「新手盲盒」，6 个盒子选 1 个随机得一只宠物（普通款保底，按下面的稀有率出稀有）。
        积分抽盒、保底、重复返还先把规则记在这里，功能以后按这些数做。
      </div>

      {row('新手盲盒稀有率', num(r.newbieRareRate, v => set('newbieRareRate', v), '%'))}
      {row('积分抽盒', <>
        {num(r.costSingle, v => set('costSingle', v), '积分 / 单抽')}
        {num(r.costTen, v => set('costTen', v), '积分 / 十连')}
      </>, true)}
      {row('稀有度概率', <>
        {Object.keys(labels).map(k => num(r.prob[k], v => setIn('prob', k, v), `% ${labels[k]}`, 70, k))}
        <span className="small" style={{ color: Math.abs(sum - 100) < 0.001 ? 'var(--ok)' : 'var(--danger)' }}>合计 {sum}%</span>
      </>, true)}
      {row('保底', <>
        {num(r.pityEpic, v => set('pityEpic', v), '抽必出史诗以上')}
        {num(r.pityLegend, v => set('pityLegend', v), '抽必出传说')}
      </>, true)}
      {row('重复自动返还积分', Object.keys(labels).map(k => num(r.refund[k], v => setIn('refund', k, v), labels[k], 80, k)), true)}
      {row('赛季限定',
        <input className="input" value={r.seasonNote} onChange={e => set('seasonNote', e.target.value)} style={{ flex: 1, minWidth: 240 }} />, true)}

      <div style={{ fontSize: 13, margin: '20px 0 10px' }}>宠物图鉴（名字、稀有度可改）</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
        {r.pets.map(p => (
          <div key={p.id} style={{ border: '1px solid var(--line)', borderRadius: 14, padding: 10, textAlign: 'center' }}>
            <img src={`${import.meta.env.BASE_URL}pets/${p.id}.png`} alt={p.name}
              style={{ width: 72, height: 72, objectFit: 'contain' }} />
            <input className="input" value={p.name} maxLength={12} onChange={e => setPet(p.id, 'name', e.target.value)}
              style={{ marginTop: 8, textAlign: 'center' }} />
            <select className="input" value={p.rarity} onChange={e => setPet(p.id, 'rarity', e.target.value)} style={{ marginTop: 6 }}>
              {Object.keys(labels).map(k => <option key={k} value={k}>{labels[k]}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div className="row" style={{ marginTop: 20 }}>
        <button className="btn btn-primary" onClick={save} disabled={state === 'saving'}>{state === 'saving' ? '保存中…' : '保存规则'}</button>
        {state === 'saved' && <span className="small" style={{ color: 'var(--ok)' }}>已保存</span>}
        {state && !['saving', 'saved'].includes(state) && <span className="small" style={{ color: 'var(--danger)' }}>{state}</span>}
      </div>
    </div>
  )
}

function SkillPanel() {
  const [content, setContent] = useState('')
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEffect(() => {
    let alive = true
    api.getSkill().then(d => { if (alive) setContent(d?.content || '') }).catch(e => { if (alive) setErr(e.message) })
    return () => { alive = false }
  }, [])
  async function save() {
    setBusy(true); setErr(''); setSaved('')
    try {
      const r = await api.saveSkill(content)
      setSaved(`已保存（${r?.chars ?? content.length} 字），下一轮生效`)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  return (
    <section>
      <h2>Prompt（系统提示词）</h2>
      <div className="muted small" style={{ marginBottom: 10 }}>
        这份正文就是 AI 的 system prompt（预设式，1–3k 字），细节由知识库【参考资料】兜。保存后<b>下一轮生效</b>。
      </div>
      {err && <div className="err small" style={{ marginBottom: 8 }}>{err}</div>}
      {saved && <div className="ok small" style={{ marginBottom: 8 }}>{saved}</div>}
      <textarea value={content} onChange={e => setContent(e.target.value)} rows={26}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5, padding: 10 }} />
      <div className="row" style={{ marginTop: 10, alignItems: 'center', gap: 12 }}>
        <button className="btn" disabled={busy || !content} onClick={save}>{busy ? '保存中…' : '保存'}</button>
        <span className="muted small">{content.length} 字</span>
      </div>
    </section>
  )
}

function SkillSettingsPanel() {
  const [s, setS] = useState(null)
  const [root, setRoot] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  useEffect(() => {
    api.getSkillSettings().then(d => { setS(d); setRoot(d.root || '') }).catch(e => setErr(e.message))
  }, [])
  async function save(patch) {
    setBusy(true); setErr(''); setMsg('')
    try {
      const d = await api.saveSkillSettings({ root, ...patch })
      setS(d); setMsg('已保存')
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  if (err && !s) return <section><div className="err">{err}</div></section>
  if (!s) return <section><div className="muted">加载中…</div></section>
  return (
    <section>
      <h2>Skill 扩展（外挂块库）</h2>
      <div className="muted small" style={{ marginBottom: 12 }}>
        框架内知识库（问答 + 文档）默认生效；这里是可选的「32 块明细」。关掉后只走知识库与规则文档，不会报错。
      </div>
      <div style={{ display: 'grid', gap: 12, maxWidth: 720 }}>
        <label className="row" style={{ gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={!!s.enabled} disabled={busy}
            onChange={e => save({ enabled: e.target.checked })} />
          <span>启用外挂 skill（命中块时读全文注入）</span>
        </label>
        <label>
          <div className="muted small" style={{ marginBottom: 4 }}>Skill 目录（SKILL_ROOT）</div>
          <input className="input" value={root} onChange={e => setRoot(e.target.value)} style={{ width: '100%' }} />
        </label>
        <div className="row" style={{ gap: 12, alignItems: 'center' }}>
          <button className="btn" disabled={busy} onClick={() => save({ enabled: s.enabled })}>保存目录</button>
          <span className="muted small">
            块 {s.blocks} 个 / 目录 {s.dirs} 个 / {s.exists ? '路径有效' : '路径无效'} / 启用文档 {s.enabledDocs} 篇
          </span>
        </div>
        {msg && <div className="ok small">{msg}</div>}
        {err && <div className="err small">{err}</div>}
        <div className="muted small">
          部署到服务器后，目录一般是 <code>/opt/etc-server/skills/etc-cs</code>；改完保存即生效，不用重启。
        </div>
      </div>
    </section>
  )
}
